export const translations = {
  en: {
    // Navigation
    nav: {
      features: 'Features',
      howItWorks: 'How It Works',
      calculator: 'Tools',
      blog: 'Insights',
      about: 'About',
      getStarted: 'Get Early Access'
    },
    
    // Hero Section - AI Financial Co-Pilot
    hero: {
      badge: 'AI Financial Intelligence Platform',
      title1: 'Your AI',
      title2: 'Financial Co-Pilot',
      subtitle: 'Qykfin helps you choose the best credit cards, maximize rewards, optimize payments, and make smarter financial decisions — all powered by AI.',
      emailPlaceholder: 'Enter your email',
      joinWaitlist: 'Join Waitlist',
      joining: 'Joining...',
      trustBadge1: 'AI-Powered Intelligence',
      trustBadge2: 'Bank-Grade Security',
      trustBadge3: 'UAE Focused',
      // App mockup
      appTitle: 'Financial Intelligence',
      missedRewards: 'Missed Rewards This Month',
      missedAmount: 'AED 184',
      suggestion: 'Use ADCB Cashback for dining',
      smartPaySaving: 'Pay AED 2,100 today',
      savingAmount: 'Save AED 310 in interest',
      creditScore: 'Credit Health',
      scoreValue: 'Excellent',
      utilization: 'Utilization at 24%'
    },
    
    // Problem Section - Why People Lose Money
    problem: {
      badge: 'The Hidden Problem',
      title: 'Most UAE residents lose money without knowing it',
      subtitle: 'Traditional finance is designed to be confusing. Here\'s what\'s silently costing you:',
      problems: [
        {
          title: 'Wrong Card Usage',
          desc: 'Using the wrong card for purchases means missing out on cashback, miles, and rewards you\'ve already earned.',
          stat: '73%',
          statLabel: 'use suboptimal cards'
        },
        {
          title: 'Hidden Interest Charges',
          desc: 'Minimum payments and poor timing lead to compound interest that quietly grows your debt.',
          stat: 'AED 2,400+',
          statLabel: 'avg. yearly loss'
        },
        {
          title: 'Missed Reward Opportunities',
          desc: 'Points expire, cashback categories change, and promotional rates end — all without notification.',
          stat: '42%',
          statLabel: 'rewards go unclaimed'
        },
        {
          title: 'Poor Payment Timing',
          desc: 'Paying bills at the wrong time affects credit utilization and costs you in hidden fees.',
          stat: '8-15%',
          statLabel: 'higher effective APR'
        }
      ]
    },
    
    // Solution Section - How Qykfin Fixes This
    solution: {
      badge: 'The Qykfin Solution',
      title: 'AI that works for your finances, 24/7',
      subtitle: 'Qykfin\'s intelligence engine analyzes your financial behavior and optimizes every decision automatically.',
      features: [
        {
          title: 'Real-time Card Optimization',
          desc: 'Always know which card to use for maximum rewards on every purchase.'
        },
        {
          title: 'Smart Payment Scheduling',
          desc: 'AI calculates the optimal payment timing to minimize interest and maximize credit score.'
        },
        {
          title: 'Proactive Alerts',
          desc: 'Get notified before rates change, rewards expire, or better opportunities arise.'
        },
        {
          title: 'Personalized Insights',
          desc: 'Recommendations that learn from your spending patterns and financial goals.'
        }
      ]
    },
    
    // Features Grid - Core Capabilities
    features: {
      badge: 'Capabilities',
      title: 'Everything you need. One intelligent platform.',
      subtitle: 'Six powerful AI modules working together to optimize your financial life.',
      
      // Feature 1: Credit Card Intelligence
      cardIntel: {
        title: 'AI Credit Card Intelligence',
        desc: 'Find the perfect card for your lifestyle. Our AI matches you with cards based on your spending patterns, rewards preferences, and eligibility.',
        bullets: ['Best card recommendations', 'Eligibility pre-check', 'Reward optimization', 'Cashback & miles analysis']
      },
      
      // Feature 2: Rewards Engine
      rewards: {
        title: 'Rewards Intelligence Engine',
        desc: 'Stop leaving money on the table. Track, optimize, and maximize every reward point across all your cards.',
        bullets: ['Track missed rewards', 'Best card for every spend', 'Reward simulation', 'Expiry alerts']
      },
      
      // Feature 3: Smart Pay
      smartPay: {
        title: 'Smart Pay AI',
        desc: 'Intelligent payment distribution that reduces interest, improves utilization, and protects your credit score.',
        bullets: ['Optimize bill payments', 'Reduce interest burden', 'Improve utilization', 'Avoid late fees']
      },
      
      // Feature 4: Dashboard
      dashboard: {
        title: 'Unified Financial Dashboard',
        desc: 'All your financial accounts in one intelligent view. Cards, loans, mortgages, EMIs — always in sync.',
        bullets: ['All accounts unified', 'Real-time balances', 'Upcoming dues', 'Financial health score']
      },
      
      // Feature 5: AI Assistant
      assistant: {
        title: 'AI Financial Assistant',
        desc: 'Your personal financial advisor, available 24/7. Ask questions, get personalized guidance, make better decisions.',
        bullets: ['Natural language queries', 'Personalized advice', 'Scenario planning', 'Goal tracking']
      },
      
      // Feature 6: Mortgage & Refinance
      mortgage: {
        title: 'Mortgage & Refinance Intelligence',
        desc: 'Whether buying or refinancing, our AI finds the best rates and terms from UAE banks for your profile.',
        bullets: ['Compare all options', 'Refinance analysis', 'EMI optimization', 'Eligibility matching']
      }
    },
    
    // AI Engine Section
    aiEngine: {
      badge: 'Powered by Intelligence',
      title: 'Not just another finance app.',
      titleHighlight: 'An AI Financial Intelligence Engine.',
      subtitle: 'Qykfin processes millions of data points to understand your financial behavior and optimize every decision.',
      capabilities: [
        { title: 'Decision Engine', desc: 'Analyzes 50+ factors for every recommendation' },
        { title: 'Behavioral Intelligence', desc: 'Learns your patterns to predict needs' },
        { title: 'Real-time Processing', desc: 'Market rates and opportunities updated continuously' },
        { title: 'Privacy-First', desc: 'Your data is encrypted and never sold' }
      ]
    },
    
    // Dashboard Preview Section
    dashboardPreview: {
      badge: 'Unified View',
      title: 'Your complete financial picture',
      subtitle: 'See everything that matters in one intelligent dashboard.',
      metrics: {
        totalBalance: 'Total Balance',
        monthlySpend: 'Monthly Spend',
        rewardsEarned: 'Rewards Earned',
        creditHealth: 'Credit Health'
      }
    },
    
    // Rewards Preview Section  
    rewardsPreview: {
      badge: 'Rewards Intelligence',
      title: 'Never miss a reward again',
      subtitle: 'Qykfin tracks every opportunity and tells you exactly which card to use.',
      examples: [
        { category: 'Dining', card: 'ADCB Touchpoints', reward: '5% cashback' },
        { category: 'Travel', card: 'Emirates NBD Skywards', reward: '3x miles' },
        { category: 'Groceries', card: 'FAB Cashback', reward: '2% back' },
        { category: 'Online', card: 'Mashreq Solitaire', reward: '4% cashback' }
      ]
    },
    
    // Smart Pay Preview Section
    smartPayPreview: {
      badge: 'Payment Intelligence',
      title: 'Pay smarter, not harder',
      subtitle: 'AI optimizes every payment to minimize interest and maximize your financial health.',
      demo: {
        title: 'Smart Payment Recommendation',
        description: 'Based on your balances and due dates, here\'s how to optimize:',
        action: 'Pay AED 2,100 to Credit Card',
        benefit: 'Saves AED 310 in interest this month',
        utilizationBefore: 'Utilization: 68%',
        utilizationAfter: 'After payment: 24%'
      }
    },
    
    // Why Qykfin Section
    whyQykfin: {
      badge: 'Why Qykfin',
      title: 'Traditional finance apps are broken',
      subtitle: 'They show you data. We give you intelligence.',
      comparison: {
        traditional: {
          title: 'Traditional Apps',
          points: [
            'Show balances (that\'s it)',
            'No intelligent insights',
            'Fragmented across apps',
            'Reactive, not proactive',
            'Generic recommendations'
          ]
        },
        qykfin: {
          title: 'Qykfin Intelligence',
          points: [
            'Optimize every decision',
            'AI-powered insights',
            'Everything unified',
            'Proactive guidance',
            'Personalized to you'
          ]
        }
      }
    },
    
    // Waitlist Section
    waitlist: {
      badge: 'Early Access',
      title: 'Join the future of financial intelligence',
      subtitle: 'Get early access to Qykfin and experience AI-powered financial optimization before public launch.',
      benefits: ['Free for 6 months for first 100 users', 'Priority feature access', 'Direct founder support'],
      namePlaceholder: 'Full Name',
      emailPlaceholder: 'Email Address',
      userTypes: {
        user: 'Individual User',
        investor: 'Investor',
        partner: 'Potential Partner'
      },
      cta: 'Get Early Access',
      privacy: 'Your data is secure. We never spam.',
      successTitle: 'You\'re on the list!',
      successSubtitle: 'We\'ll be in touch soon with your early access.'
    },
    
    // Footer
    footer: {
      tagline: 'AI-Powered Financial Intelligence for UAE',
      links: {
        product: 'Product',
        features: 'Features',
        calculator: 'Loan Calculator',
        blog: 'Blog',
        company: 'Company',
        about: 'About Us',
        careers: 'Careers',
        contact: 'Contact',
        legal: 'Legal',
        privacy: 'Privacy Policy',
        terms: 'Terms of Service'
      },
      copyright: '© 2026 Qykfin. All rights reserved.',
      madeIn: 'Made with intelligence in UAE'
    },
    
    // Calculator Section (kept but demoted)
    calculator: {
      badge: 'Financial Tools',
      title: 'Smart Loan Calculator',
      subtitle: 'Get AI-powered loan calculations with live UAE bank rates.',
      home: 'Home Loan',
      motor: 'Auto Loan',
      personal: 'Personal Loan',
      tryCalculator: 'Try Full Calculator',
      propertyValue: 'Property Value',
      carValue: 'Vehicle Value',
      loanAmount: 'Loan Amount',
      downPayment: 'Down Payment',
      interestRate: 'Interest Rate',
      tenure: 'Tenure (Years)',
      monthlySalary: 'Monthly Salary (Optional)',
      calculateEMI: 'Calculate EMI',
      calculating: 'Calculating...',
      monthlyEMI: 'Monthly EMI',
      getAIAnalysis: 'Get AI Analysis'
    }
  },
  
  // Arabic Translations
  ar: {
    // Navigation
    nav: {
      features: 'المميزات',
      howItWorks: 'كيف يعمل',
      calculator: 'الأدوات',
      blog: 'المقالات',
      about: 'من نحن',
      getStarted: 'احصل على وصول مبكر'
    },
    
    // Hero Section
    hero: {
      badge: 'منصة الذكاء المالي الاصطناعي',
      title1: 'مساعدك المالي',
      title2: 'الذكي',
      subtitle: 'كيكفن يساعدك على اختيار أفضل البطاقات الائتمانية، تعظيم المكافآت، تحسين المدفوعات، واتخاذ قرارات مالية أذكى — كل ذلك بقوة الذكاء الاصطناعي.',
      emailPlaceholder: 'أدخل بريدك الإلكتروني',
      joinWaitlist: 'انضم للقائمة',
      joining: 'جارٍ الانضمام...',
      trustBadge1: 'ذكاء اصطناعي متقدم',
      trustBadge2: 'أمان بمستوى البنوك',
      trustBadge3: 'مصمم للإمارات',
      appTitle: 'الذكاء المالي',
      missedRewards: 'المكافآت الفائتة هذا الشهر',
      missedAmount: '184 درهم',
      suggestion: 'استخدم بطاقة ADCB للمطاعم',
      smartPaySaving: 'ادفع 2,100 درهم اليوم',
      savingAmount: 'وفر 310 درهم من الفوائد',
      creditScore: 'الصحة الائتمانية',
      scoreValue: 'ممتاز',
      utilization: 'نسبة الاستخدام 24%'
    },
    
    // Problem Section
    problem: {
      badge: 'المشكلة الخفية',
      title: 'معظم سكان الإمارات يخسرون المال دون علمهم',
      subtitle: 'التمويل التقليدي مصمم ليكون معقداً. إليك ما يكلفك بصمت:',
      problems: [
        {
          title: 'استخدام البطاقة الخاطئة',
          desc: 'استخدام البطاقة الخاطئة للمشتريات يعني خسارة الاسترداد النقدي والأميال والمكافآت.',
          stat: '73%',
          statLabel: 'يستخدمون بطاقات غير مثالية'
        },
        {
          title: 'رسوم فائدة مخفية',
          desc: 'الدفعات الدنيا والتوقيت السيء يؤديان إلى فوائد مركبة تنمي ديونك بصمت.',
          stat: '+2,400 درهم',
          statLabel: 'متوسط الخسارة السنوية'
        },
        {
          title: 'فرص مكافآت ضائعة',
          desc: 'النقاط تنتهي، فئات الاسترداد تتغير، والعروض الترويجية تنتهي — كل ذلك بدون إشعار.',
          stat: '42%',
          statLabel: 'من المكافآت لا تُستخدم'
        },
        {
          title: 'توقيت دفع سيء',
          desc: 'دفع الفواتير في الوقت الخاطئ يؤثر على نسبة الاستخدام ويكلفك رسوماً خفية.',
          stat: '8-15%',
          statLabel: 'معدل فائدة فعلي أعلى'
        }
      ]
    },
    
    // Solution Section
    solution: {
      badge: 'حل كيكفن',
      title: 'ذكاء اصطناعي يعمل لأموالك على مدار الساعة',
      subtitle: 'محرك الذكاء في كيكفن يحلل سلوكك المالي ويحسّن كل قرار تلقائياً.',
      features: [
        {
          title: 'تحسين البطاقات في الوقت الفعلي',
          desc: 'اعرف دائماً أي بطاقة تستخدم لأقصى مكافآت على كل عملية شراء.'
        },
        {
          title: 'جدولة ذكية للمدفوعات',
          desc: 'الذكاء الاصطناعي يحسب التوقيت الأمثل للدفع لتقليل الفوائد وتحسين درجتك الائتمانية.'
        },
        {
          title: 'تنبيهات استباقية',
          desc: 'احصل على إشعارات قبل تغير الأسعار أو انتهاء المكافآت أو ظهور فرص أفضل.'
        },
        {
          title: 'رؤى مخصصة',
          desc: 'توصيات تتعلم من أنماط إنفاقك وأهدافك المالية.'
        }
      ]
    },
    
    // Features Grid
    features: {
      badge: 'القدرات',
      title: 'كل ما تحتاجه. منصة ذكية واحدة.',
      subtitle: 'ست وحدات ذكاء اصطناعي تعمل معاً لتحسين حياتك المالية.',
      
      cardIntel: {
        title: 'ذكاء البطاقات الائتمانية',
        desc: 'اعثر على البطاقة المثالية لأسلوب حياتك. ذكاؤنا الاصطناعي يطابقك مع البطاقات بناءً على أنماط إنفاقك.',
        bullets: ['أفضل توصيات البطاقات', 'فحص الأهلية المسبق', 'تحسين المكافآت', 'تحليل الاسترداد والأميال']
      },
      
      rewards: {
        title: 'محرك ذكاء المكافآت',
        desc: 'توقف عن ترك المال على الطاولة. تتبع وحسّن وعظّم كل نقطة مكافأة عبر جميع بطاقاتك.',
        bullets: ['تتبع المكافآت الفائتة', 'أفضل بطاقة لكل إنفاق', 'محاكاة المكافآت', 'تنبيهات الانتهاء']
      },
      
      smartPay: {
        title: 'الدفع الذكي',
        desc: 'توزيع ذكي للمدفوعات يقلل الفوائد ويحسّن نسبة الاستخدام ويحمي درجتك الائتمانية.',
        bullets: ['تحسين دفع الفواتير', 'تقليل عبء الفوائد', 'تحسين الاستخدام', 'تجنب الرسوم المتأخرة']
      },
      
      dashboard: {
        title: 'لوحة مالية موحدة',
        desc: 'جميع حساباتك المالية في عرض ذكي واحد. البطاقات، القروض، الرهون، الأقساط — دائماً متزامنة.',
        bullets: ['توحيد جميع الحسابات', 'أرصدة فورية', 'المستحقات القادمة', 'درجة الصحة المالية']
      },
      
      assistant: {
        title: 'المساعد المالي الذكي',
        desc: 'مستشارك المالي الشخصي، متاح على مدار الساعة. اطرح أسئلة، احصل على إرشادات مخصصة، اتخذ قرارات أفضل.',
        bullets: ['استفسارات بلغة طبيعية', 'نصائح مخصصة', 'تخطيط السيناريوهات', 'تتبع الأهداف']
      },
      
      mortgage: {
        title: 'ذكاء الرهن وإعادة التمويل',
        desc: 'سواء كنت تشتري أو تعيد التمويل، ذكاؤنا الاصطناعي يجد أفضل الأسعار والشروط من بنوك الإمارات لملفك.',
        bullets: ['مقارنة جميع الخيارات', 'تحليل إعادة التمويل', 'تحسين الأقساط', 'مطابقة الأهلية']
      }
    },
    
    // AI Engine Section
    aiEngine: {
      badge: 'مدعوم بالذكاء',
      title: 'ليس مجرد تطبيق مالي آخر.',
      titleHighlight: 'محرك ذكاء مالي اصطناعي.',
      subtitle: 'كيكفن يعالج ملايين نقاط البيانات لفهم سلوكك المالي وتحسين كل قرار.',
      capabilities: [
        { title: 'محرك القرارات', desc: 'يحلل أكثر من 50 عاملاً لكل توصية' },
        { title: 'الذكاء السلوكي', desc: 'يتعلم أنماطك للتنبؤ باحتياجاتك' },
        { title: 'المعالجة الفورية', desc: 'أسعار السوق والفرص محدثة باستمرار' },
        { title: 'الخصوصية أولاً', desc: 'بياناتك مشفرة ولا تُباع أبداً' }
      ]
    },
    
    // Dashboard Preview
    dashboardPreview: {
      badge: 'عرض موحد',
      title: 'صورتك المالية الكاملة',
      subtitle: 'شاهد كل ما يهم في لوحة ذكية واحدة.',
      metrics: {
        totalBalance: 'الرصيد الإجمالي',
        monthlySpend: 'الإنفاق الشهري',
        rewardsEarned: 'المكافآت المكتسبة',
        creditHealth: 'الصحة الائتمانية'
      }
    },
    
    // Rewards Preview
    rewardsPreview: {
      badge: 'ذكاء المكافآت',
      title: 'لا تفوّت مكافأة مرة أخرى',
      subtitle: 'كيكفن يتتبع كل فرصة ويخبرك بالضبط أي بطاقة تستخدم.',
      examples: [
        { category: 'المطاعم', card: 'ADCB Touchpoints', reward: '5% استرداد' },
        { category: 'السفر', card: 'Emirates NBD Skywards', reward: '3x أميال' },
        { category: 'البقالة', card: 'FAB Cashback', reward: '2% استرداد' },
        { category: 'أونلاين', card: 'Mashreq Solitaire', reward: '4% استرداد' }
      ]
    },
    
    // Smart Pay Preview
    smartPayPreview: {
      badge: 'ذكاء المدفوعات',
      title: 'ادفع بذكاء، ليس بجهد',
      subtitle: 'الذكاء الاصطناعي يحسّن كل دفعة لتقليل الفوائد وتعزيز صحتك المالية.',
      demo: {
        title: 'توصية الدفع الذكي',
        description: 'بناءً على أرصدتك وتواريخ الاستحقاق، إليك كيفية التحسين:',
        action: 'ادفع 2,100 درهم لبطاقة الائتمان',
        benefit: 'يوفر 310 درهم من الفوائد هذا الشهر',
        utilizationBefore: 'نسبة الاستخدام: 68%',
        utilizationAfter: 'بعد الدفع: 24%'
      }
    },
    
    // Why Qykfin
    whyQykfin: {
      badge: 'لماذا كيكفن',
      title: 'تطبيقات التمويل التقليدية معطلة',
      subtitle: 'هي تعرض لك البيانات. نحن نعطيك الذكاء.',
      comparison: {
        traditional: {
          title: 'التطبيقات التقليدية',
          points: [
            'تعرض الأرصدة (فقط)',
            'لا رؤى ذكية',
            'مجزأة عبر التطبيقات',
            'تفاعلية، ليست استباقية',
            'توصيات عامة'
          ]
        },
        qykfin: {
          title: 'ذكاء كيكفن',
          points: [
            'يحسّن كل قرار',
            'رؤى بالذكاء الاصطناعي',
            'كل شيء موحد',
            'إرشاد استباقي',
            'مخصص لك'
          ]
        }
      }
    },
    
    // Waitlist Section
    waitlist: {
      badge: 'وصول مبكر',
      title: 'انضم إلى مستقبل الذكاء المالي',
      subtitle: 'احصل على وصول مبكر لكيكفن واختبر التحسين المالي بالذكاء الاصطناعي قبل الإطلاق العام.',
      benefits: ['مجاني لمدة 6 أشهر لأول 100 مستخدم', 'أولوية الوصول للميزات', 'دعم مباشر من المؤسسين'],
      namePlaceholder: 'الاسم الكامل',
      emailPlaceholder: 'البريد الإلكتروني',
      userTypes: {
        user: 'مستخدم فردي',
        investor: 'مستثمر',
        partner: 'شريك محتمل'
      },
      cta: 'احصل على وصول مبكر',
      privacy: 'بياناتك آمنة. لا نرسل رسائل مزعجة.',
      successTitle: 'أنت في القائمة!',
      successSubtitle: 'سنتواصل معك قريباً بخصوص وصولك المبكر.'
    },
    
    // Footer
    footer: {
      tagline: 'ذكاء مالي اصطناعي للإمارات',
      links: {
        product: 'المنتج',
        features: 'المميزات',
        calculator: 'حاسبة القروض',
        blog: 'المدونة',
        company: 'الشركة',
        about: 'من نحن',
        careers: 'الوظائف',
        contact: 'تواصل معنا',
        legal: 'قانوني',
        privacy: 'سياسة الخصوصية',
        terms: 'شروط الخدمة'
      },
      copyright: '© 2026 كيكفن. جميع الحقوق محفوظة.',
      madeIn: 'صنع بذكاء في الإمارات'
    },
    
    // Calculator Section
    calculator: {
      badge: 'الأدوات المالية',
      title: 'حاسبة القروض الذكية',
      subtitle: 'احصل على حسابات قروض مدعومة بالذكاء الاصطناعي مع أسعار بنوك الإمارات.',
      home: 'قرض سكني',
      motor: 'قرض سيارة',
      personal: 'قرض شخصي',
      tryCalculator: 'جرب الحاسبة الكاملة',
      propertyValue: 'قيمة العقار',
      carValue: 'قيمة السيارة',
      loanAmount: 'مبلغ القرض',
      downPayment: 'الدفعة الأولى',
      interestRate: 'نسبة الفائدة',
      tenure: 'المدة (سنوات)',
      monthlySalary: 'الراتب الشهري (اختياري)',
      calculateEMI: 'احسب القسط',
      calculating: 'جارٍ الحساب...',
      monthlyEMI: 'القسط الشهري',
      getAIAnalysis: 'احصل على تحليل ذكي'
    }
  }
};

export default translations;
